import React, {useEffect, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import NobulaLogo from '../../../../../assets/icons/NobulaLogo.svg';
import {loggedInUserSelector} from '../../../../store/selectors/auth.selector';
import {hasSlidesSelector, presentationStatusSelector} from '../../../../store/selectors/presentation.selectors';
import Modal from '../../../../ui/components/common/Modal';
import Button from '../../../../incase-domain/components/button/Button';
import {handleLoginFormSubmit} from '../../../../util/loginFormUtils';
import {GuestCredentials} from './GuestModal';
import {TypedTranslation} from '../../../../i18n';

interface AdminLoginModalProps {
    isRoomAdmin: boolean;
    isRoomPresenterAnonymous?: boolean;
    findPresentation: (cr?: GuestCredentials) => void;
}

function AdminLoginModal({isRoomAdmin, isRoomPresenterAnonymous, findPresentation}: AdminLoginModalProps): JSX.Element {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const loggedInUser = useSelector(loggedInUserSelector);
    const presentationStatus = useSelector(presentationStatusSelector);
    const hasSlides = useSelector(hasSlidesSelector);

    const [loginModalOpen, setLoginModalOpen] = useState(false);
    const [loginLoader, setLoginLoader] = useState(false);
    const [loginError, setLoginError] = useState<TypedTranslation | undefined>();

    const isActiveAdmin = isRoomAdmin && !isRoomPresenterAnonymous;
    const isPresentationDownloaded = presentationStatus.found && hasSlides;
    const isAuthenticatedOrPresentationLoaded = loggedInUser || isPresentationDownloaded;

    const shouldOpenLoginModal = isActiveAdmin && !isAuthenticatedOrPresentationLoaded;

    useEffect(() => {
        setLoginModalOpen(shouldOpenLoginModal);
    }, [shouldOpenLoginModal]);

    useEffect(() => {
        if (isRoomAdmin && loggedInUser) {
            findPresentation();
        }
    }, [loggedInUser, isRoomAdmin, findPresentation]);

    const closeModal = (): void => {
        setLoginModalOpen(false);
        navigate('/');
    };

    return (
        <Modal
            isOpen={loginModalOpen}
            header={
                <div className="c-credentialsModal__header u-mt-2">
                    <img src={NobulaLogo} alt="Nobula case logo" />
                </div>
            }
            onCancel={closeModal}
            blockOverlayClose
        >
            <div className="c-credentialsModal__body">
                <div className="c-credentialsModal__body_title">
                    <Trans i18nKey="common.welcome" />
                </div>

                <form
                    className={`c-form ${loginError ? 'is-invalid' : ''}`}
                    onSubmit={(e) => handleLoginFormSubmit(e, setLoginError, setLoginLoader, findPresentation, dispatch)}
                >
                    <div className="c-field">
                        <label>
                            <div className="c-credentialsModal__body_label">{t('form.emailOrusername')}</div>
                            <div className="c-control">
                                <input
                                    type="text"
                                    name="username"
                                    className="e-input"
                                    placeholder={t('form.emailOrusername')}
                                    data-cy="username"
                                />
                            </div>
                        </label>
                    </div>

                    <div className="c-field">
                        <label>
                            <div className="c-credentialsModal__body_label">{t('form.password')}</div>
                            <div className="c-control">
                                <input
                                    type="password"
                                    name="password"
                                    className="e-input"
                                    placeholder={t('form.password')}
                                    data-cy="password"
                                />
                            </div>
                        </label>
                    </div>

                    {loginError && <span className="c-form-help is-invalid">{t(loginError)}</span>}

                    <div className="u-mt-3">
                        <Button
                            fullWidth
                            variant="solid"
                            size="lg"
                            loading={loginLoader}
                            color="secondary"
                            dataCy="loginSubmit"
                            type="submit"
                        >
                            {t('form.login')}
                        </Button>
                    </div>
                </form>
            </div>
        </Modal>
    );
}

export default AdminLoginModal;
